import { createFileRoute } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import TitleRow from '@/components/ui/TitleRow'
import CompareView from '@/screens/brokers/components/compare/CompareView'
import { loadBrokersCompare } from '@/screens/brokers/CompareScreen.loader'
import { compareSearchSchema } from '@/screens/brokers/search'

/**
 * No `pendingComponent` / `errorComponent` of its own: this route's loader
 * awaits only the directory, and only on an arrival with no picks, to redirect
 * to the default pair (see `CompareScreen.loader.ts`); it never throws an
 * error. The router's default `PendingPage` covers that short wait, as it does
 * a cold load of the route's code.
 */
export const Route = createFileRoute('/_app/brokers/compare')({
  validateSearch: compareSearchSchema,
  loader: loadBrokersCompare,
  component: CompareScreen,
})

/**
 * Brokers · Compare (Figma `3167:2`, with `3170:2` / `3170:688` / `3170:1372`).
 *
 * The route and nothing else: the URL is read here, the selection is written
 * back here, and everything drawn below the family header lives in
 * `components/compare/CompareView.tsx` — which, unlike this file, a test can
 * import (this one calls `createFileRoute`, and `src/routeTree.gen.ts` is
 * generated rather than committed).
 *
 * Rebuilt to the revamp mock (`#/brokers/compare`, 2026-10-28): the body is
 * `CompareView`; the Broker rail comes from `ScreenLayout` by route. Arriving
 * with no picks pre-picks two houses (`CompareScreen.loader.ts`).
 *
 * `codes` is 2-4 distinct broker codes or absent; `compareCodesSchema` accepts
 * both the array TanStack Router JSON-round-trips through `navigate()` and the
 * comma string a pasted link carries, and always hands back an array — so
 * nothing here re-parses it.
 *
 * `replace: true` on every SELECTION write: adding and removing brokers is one
 * member refining one comparison, and a history entry per card would make Back
 * walk their whole shortlist in reverse before leaving the screen. `undefined`
 * drops `codes` from the URL entirely, which is the only thing a selection of
 * fewer than two brokers can be — see `publishedCodes`. The sub-tab is an
 * ordinary navigation: that IS a step a member expects Back to retrace.
 */
function CompareScreen() {
  const { codes, tab } = Route.useSearch()
  const navigate = Route.useNavigate()

  return (
    <ScreenLayout header={<TitleRow title="Compare" />} pageTitle="Compare">
      <CompareView
        codes={codes ?? []}
        onCodesChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, codes: next }), replace: true })
        }}
        tab={tab}
        onTabChange={(next) => {
          void navigate({ search: (prev) => ({ ...prev, tab: next }) })
        }}
      />
    </ScreenLayout>
  )
}
